import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  fetchNotifications as fetchNotificationsAction,
  fetchFollowUpNotifications as fetchFollowUpNotificationsAction,
  fetchStalledClaimNotifications as fetchStalledClaimNotificationsAction,
  fetchClaimEmailApprovalNotifications as fetchClaimEmailApprovalNotificationsAction,
  fetchClaimCreatedNotifications as fetchClaimCreatedNotificationsAction,
  markNotificationRead,
  CLAIM_EMAIL_APPROVAL_NOTIFICATION_TYPE,
  CLAIM_CREATED_NOTIFICATION_TYPE,
  FOLLOW_UP_NOTIFICATION_TYPE,
  STALLED_CLAIM_NOTIFICATION_TYPE,
  WHATS_NEW_NOTIFICATION_TYPE,
} from '../../../views/alerts/actions';
import { showDialog } from '../../actions';
import {
  getNotificationType,
  getUnreadTrackingCount,
  getAutoLoginNotifications,
} from './notificationHelpers';

interface UseNotificationsProps {
  primarySiteId: number;
  whatsNewNotifications: any[];
  followUpNotifications: any[];
  stalledClaimNotifications: any[];
  claimEmailApprovalNotifications: any[];
  claimCreatedNotifications: any[];
}

export const useNotifications = ({
  primarySiteId,
  whatsNewNotifications,
  followUpNotifications,
  stalledClaimNotifications,
  claimEmailApprovalNotifications,
  claimCreatedNotifications,
}: UseNotificationsProps) => {
  const dispatch = useDispatch();
  const [autoQueueKey, setAutoQueueKey] = useState<string | null>(null);

  const readTrackingCount = useMemo(
    () => getUnreadTrackingCount(whatsNewNotifications),
    [whatsNewNotifications]
  );

  const followUpUnreadCount = useMemo(
    () => getUnreadTrackingCount(followUpNotifications),
    [followUpNotifications]
  );

  const stalledClaimUnreadCount = useMemo(
    () => getUnreadTrackingCount(stalledClaimNotifications),
    [stalledClaimNotifications]
  );

  const claimEmailApprovalUnreadCount = useMemo(
    () => getUnreadTrackingCount(claimEmailApprovalNotifications),
    [claimEmailApprovalNotifications]
  );

  const claimCreatedUnreadCount = useMemo(
    () => getUnreadTrackingCount(claimCreatedNotifications),
    [claimCreatedNotifications]
  );

  const claimNotifications = useMemo(
    () => [
      ...(followUpNotifications || []),
      ...(stalledClaimNotifications || []),
      ...(claimEmailApprovalNotifications || []),
      ...(claimCreatedNotifications || []),
    ],
    [
      followUpNotifications,
      stalledClaimNotifications,
      claimEmailApprovalNotifications,
      claimCreatedNotifications,
    ]
  );

  const claimUnreadCount =
    followUpUnreadCount +
    stalledClaimUnreadCount +
    claimEmailApprovalUnreadCount +
    claimCreatedUnreadCount;

  const autoLoginNotifications = useMemo(
    () =>
      getAutoLoginNotifications([
        ...(whatsNewNotifications || []),
        ...(claimCreatedNotifications || []),
      ]),
    [whatsNewNotifications, claimCreatedNotifications]
  );

  const getRefreshNotificationAction = useCallback(
    (notification: any) => {
      const type = getNotificationType(notification);
      const query = {
        includeTracking: true,
        ...(primarySiteId ? { siteId: primarySiteId } : {}),
      };

      if (type === FOLLOW_UP_NOTIFICATION_TYPE) {
        return fetchFollowUpNotificationsAction({
          ...query,
          type: FOLLOW_UP_NOTIFICATION_TYPE,
        });
      }

      if (type === STALLED_CLAIM_NOTIFICATION_TYPE) {
        return fetchStalledClaimNotificationsAction({
          ...query,
          type: STALLED_CLAIM_NOTIFICATION_TYPE,
        });
      }

      if (type === CLAIM_EMAIL_APPROVAL_NOTIFICATION_TYPE) {
        return fetchClaimEmailApprovalNotificationsAction({
          ...query,
          type: CLAIM_EMAIL_APPROVAL_NOTIFICATION_TYPE,
        });
      }

      if (type === CLAIM_CREATED_NOTIFICATION_TYPE) {
        return fetchClaimCreatedNotificationsAction({
          ...query,
          type: CLAIM_CREATED_NOTIFICATION_TYPE,
        });
      }

      return fetchNotificationsAction({
        ...query,
        type: WHATS_NEW_NOTIFICATION_TYPE,
      });
    },
    [primarySiteId]
  );

  const markNotificationAsRead = useCallback(
    (notification: any) => {
      const trackingRecord =
        notification?.trackingRecords?.find((record: any) => record && record.read === false) ||
        notification?.trackingRecords?.[0];

      if (!notification?.id || !trackingRecord?.id || trackingRecord.read === true) {
        return;
      }

      dispatch(
        markNotificationRead(
          {
            notificationId: notification.id,
            trackingId: trackingRecord.id,
            read: true,
          },
          {
            callback: () => getRefreshNotificationAction(notification),
          }
        )
      );
    },
    [dispatch, getRefreshNotificationAction]
  );

  const handleNotificationOpen = useCallback(
    (notification: any, isAuto = false, queue?: any[]) => {
      if (!notification) return;
      dispatch(
        showDialog('WHATS_NEW_NOTIFICATION', {
          notification,
          defaultModal: false,
          ...(isAuto && queue?.length ? { notificationQueue: queue } : {}),
        })
      );
    },
    [dispatch]
  );

  const fetchWhatsNewNotifications = useCallback(() => {
    dispatch(
      fetchNotificationsAction({
        type: WHATS_NEW_NOTIFICATION_TYPE,
        includeTracking: true,
        ...(primarySiteId ? { siteId: primarySiteId } : {}),
      })
    );
  }, [dispatch, primarySiteId]);

  const fetchFollowUpNotifications = useCallback(() => {
    dispatch(
      fetchFollowUpNotificationsAction({
        type: FOLLOW_UP_NOTIFICATION_TYPE,
        includeTracking: true,
        ...(primarySiteId ? { siteId: primarySiteId } : {}),
      })
    );
  }, [dispatch, primarySiteId]);

  const fetchStalledClaimNotifications = useCallback(() => {
    dispatch(
      fetchStalledClaimNotificationsAction({
        type: STALLED_CLAIM_NOTIFICATION_TYPE,
        includeTracking: true,
        ...(primarySiteId ? { siteId: primarySiteId } : {}),
      })
    );
  }, [dispatch, primarySiteId]);

  const fetchClaimEmailApprovalNotifications = useCallback(() => {
    dispatch(
      fetchClaimEmailApprovalNotificationsAction({
        type: CLAIM_EMAIL_APPROVAL_NOTIFICATION_TYPE,
        includeTracking: true,
        ...(primarySiteId ? { siteId: primarySiteId } : {}),
      })
    );
  }, [dispatch, primarySiteId]);

  const fetchClaimCreatedNotifications = useCallback(() => {
    dispatch(
      fetchClaimCreatedNotificationsAction({
        type: CLAIM_CREATED_NOTIFICATION_TYPE,
        includeTracking: true,
        ...(primarySiteId ? { siteId: primarySiteId } : {}),
      })
    );
  }, [dispatch, primarySiteId]);

  // Fetch all notifications on mount and when primarySiteId changes
  useEffect(() => {
    fetchWhatsNewNotifications();
    fetchFollowUpNotifications();
    fetchStalledClaimNotifications();
    fetchClaimEmailApprovalNotifications();
    fetchClaimCreatedNotifications();
  }, [
    fetchWhatsNewNotifications,
    fetchFollowUpNotifications,
    fetchStalledClaimNotifications,
    fetchClaimEmailApprovalNotifications,
    fetchClaimCreatedNotifications,
  ]);

  // Listen for whats-new-updated events
  useEffect(() => {
    const handleWhatsNewUpdated = () => {
      fetchWhatsNewNotifications();
    };

    window.addEventListener('whats-new-updated', handleWhatsNewUpdated);
    return () => window.removeEventListener('whats-new-updated', handleWhatsNewUpdated);
  }, [fetchWhatsNewNotifications]);

  // Auto-open login notifications
  useEffect(() => {
    if (!autoLoginNotifications.length) {
      if (autoQueueKey !== null) {
        setAutoQueueKey(null);
      }
      return;
    }

    const queueKey = autoLoginNotifications.map((notification: any) => notification.id).join('-');

    if (queueKey !== autoQueueKey) {
      handleNotificationOpen(autoLoginNotifications[0], true, autoLoginNotifications);
      setAutoQueueKey(queueKey);
    }
  }, [autoLoginNotifications, autoQueueKey, handleNotificationOpen]);

  return {
    readTrackingCount,
    followUpUnreadCount,
    stalledClaimUnreadCount,
    claimEmailApprovalUnreadCount,
    claimCreatedUnreadCount,
    claimNotifications,
    claimUnreadCount,
    markNotificationAsRead,
    handleNotificationOpen,
    fetchWhatsNewNotifications,
  };
};
